import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefCallback } from 'react';

export type UseVisibilityParams = {
  intersectionObserverInit?: Partial<IntersectionObserverInit>;
};

export const useVisibility = <T extends Element>({
  intersectionObserverInit: {
    root = null,
    rootMargin = '0px',
    threshold = [0],
  } = {},
}: UseVisibilityParams = {}): {
  visibilityRefCallback: RefCallback<T>;
  isVisible: boolean;
  hasBeenVisible: boolean;
} => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Tracks whether this element is visible.
  const [isVisible, setIsVisible] = useState<boolean>(false);
  // Tracks whether this element has been visible since the first render of this
  // hook.
  const [hasBeenVisible, setHasBeenVisible] = useState<boolean>(false);

  const observerInit: IntersectionObserverInit = useMemo(
    () => ({
      root,
      rootMargin,
      threshold,
    }),
    [root, rootMargin, threshold],
  );

  useEffect(() => {
    return () => {
      const observer = observerRef.current;
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      setHasBeenVisible(true);
    }
  }, [setHasBeenVisible, isVisible]);

  const visibilityRefCallback: RefCallback<T> = useCallback(
    (node) => {
      const getObserver = () => {
        // If there is no observer, then create it. This ensures that we only
        // create it only once, but also means that updates to
        // intersectionObserverInit after the first render of the hook have no
        // effect).
        if (!observerRef.current) {
          observerRef.current = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
          }, observerInit);
        }

        return observerRef.current;
      };

      const observer = getObserver();
      observer.disconnect();

      if (node) {
        observer.observe(node);
      }
    },
    [observerInit],
  );

  return { visibilityRefCallback, isVisible, hasBeenVisible };
};
